/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Observable, Subject } from 'rxjs';
import { Alert, AlertData, AlertKinds } from '../model/alert.model';
import { MessageService } from './message.service';
import { equals } from '../helper/equals.helper';
import { ApplicationError } from '../model/application-error.model';
import { ObservableList } from '../util/observable-list.util';

interface AlertTimeoutHandle {
  alert: Alert;
  handle: any;
}

export class AlertsChangedEvent {
  constructor(
    public readonly addedAlerts: Alert[],
    public readonly removedAlerts: Alert[]
  ) { }
}

export class AlertService {
  private readonly alertList = new ObservableList<Alert>();
  private readonly alertsChangedSubject = new Subject<AlertsChangedEvent>();
  private timeoutHandles: AlertTimeoutHandle[] = [];

  constructor(private messageService: MessageService) { }

  get alerts(): ObservableList<Alert> {
    return this.alertList;
  }

  get alertsChanged(): Observable<AlertsChangedEvent> {
    return this.alertsChangedSubject.asObservable();
  }

  alert(data: AlertData) {
    const alert: Alert = data instanceof Alert ? data : new Alert(data);
    const alertIndex = this.alertList.firstIndex((item) => equals(item, alert));
    if (alertIndex < 0) {
      this.alertList.push(alert);
    } else {
      const similarAlert = this.alertList.get(alertIndex);
      this.clearTimeout(similarAlert);
      if (alertIndex === this.alertList.size - 1) {
        this.alertList.set(this.alertList.size - 1, alert);
      } else {
        const alertArray = this.alertList.toArray();
        alertArray.splice(alertIndex, 1);
        alertArray.push(alert);
        this.alertList.setValues(alertArray, { exclusive: true });
      }
    }
    this.setTimeout(alert);
    this.alertsChangedSubject.next(new AlertsChangedEvent([alert], []));
  }

  alertError(error: ApplicationError | any, timeout: number = 0) {
    const errorMessage = this.messageService.getMessage(ApplicationError.from(error));
    this.alert(new Alert({
      message: errorMessage,
      kind: AlertKinds.ERROR,
      timeout
    }));
  }

  discard(alert: Alert) {
    const alertIndex = this.alertList.firstIndex((item) => item === alert);
    if (alertIndex < 0) {
      return;
    }
    this.clearTimeout(alert);
    const removedAlerts = [this.alertList.get(alertIndex)];
    this.alertList.remove(alertIndex);
    this.alertsChangedSubject.next(new AlertsChangedEvent([], removedAlerts));
  }

  discardAll() {
    if (this.alertList.size === 0) {
      return;
    }
    this.alertList.forEach((alert) => {
      this.clearTimeout(alert);
    });
    const removedAlerts = this.alertList.toArray();
    this.alertList.clear();
    this.alertsChangedSubject.next(new AlertsChangedEvent([], removedAlerts));
  }

  setTimeout(alert: Alert) {
    if (!alert.timeout) {
      return;
    }
    this.clearTimeout(alert);
    const timeoutHandle = setTimeout(() => this.discard(alert), alert.timeout);
    this.timeoutHandles.push({ alert, handle: timeoutHandle });
  }

  clearTimeout(alert: Alert) {
    const timeoutHandleIndex = this.timeoutHandles.findIndex((handle) => handle.alert === alert);
    if (timeoutHandleIndex < 0) {
      return;
    }
    const timeoutHandle = this.timeoutHandles.splice(timeoutHandleIndex, 1)[0];
    clearTimeout(timeoutHandle.handle);
  }

}
